import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Logger } from '../logger';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/angular';
import { LocationProvider } from '../location/location';

type S3ClientType = import('@aws-sdk/client-s3').S3Client;

const GUMLET_URL = 'https://apps-tidy-images.gumlet.io';
const AWS_S3_URL = 'https://samantha-file-uploads.s3.us-west-2.amazonaws.com';

export interface S3ObjectMetadata {
  latitude: string | null;
  longitude: string | null;
  date: string | null;
  timezone: string | null;
  address: string | null;
}

export enum BeforeAfterMediaType {
  VIDEO = 'video',
  PHOTO = 'photo'
}

export interface S3UploadResponse {
  Bucket: string,
  Key: string,
  Location: string
};

@Injectable()
export class Aws {

  private RETRY_DELAY = 30000;

  constructor(
    private logger: Logger,
    private locationProvider: LocationProvider
  ) {}

  async saveJobMediaToAws(MediaData, mediaType: string, url: string, file?: any, exif?: any, retries = 0) {
    try {
      if (mediaType === BeforeAfterMediaType.VIDEO) {
        return await this.uploadVideoToS3(MediaData, url, file, exif);
      }

      return await this.uploadImageToS3(MediaData, url, file, exif);
    } catch (err) {
      if (retries < 3) {
        setTimeout( () => this.saveJobMediaToAws(MediaData, mediaType, url, file, retries + 1), this.RETRY_DELAY );
      } else {
        this.logger.error(err, 'Error trying to upload photo.');
      }
    }
  }

  parseCoordinate(coordString: string): string | null {
    if (!coordString) {
      return null;
    }
    const [degrees, minutes, seconds] = coordString.split(',').map((part) => {
      const [numerator, denominator] = part.split('/').map(Number);
      return numerator / denominator;
    });

    return (degrees + minutes / 60 + seconds / 3600).toString();
  }

  getLatitude(exif) {
    try {
      if (exif?.latitude) {
        return exif?.latitude?.toString();
      }
      if (exif?.GPS?.Latitude) {
        return exif?.GPS?.Latitude?.toString();
      }
      if (this.parseCoordinate(exif?.GPSLatitude)) {
        return this.parseCoordinate(exif?.GPSLatitude);
      }
      return null;
    } catch (_error) {
      return null;
    }
  }

  getLongitude(exif) {
    try {
      if (exif?.longitude) {
        return exif?.longitude?.toString();
      }
      if (exif?.GPS?.Longitude) {
        return exif?.GPS?.Longitude?.toString();
      }
      if (this.parseCoordinate(exif?.GPSLongitude)) {
        return this.parseCoordinate(exif?.GPSLongitude);
      }
      return null;
    } catch (_error) {
      return null;
    }
  }

  getDate(exif, file) {
    try {
      if (exif?.CreateDate) {
        return exif?.CreateDate?.toISOString();
      }
      if (exif?.DateTimeOriginal) {
        return DateTime.fromFormat(
          exif?.DateTimeOriginal,
          'yyyy:MM:dd HH:mm:ss'
        )?.toISO();
      }
      if (file?.modifiedAt || exif?.modifiedAt) {
        return DateTime.fromMillis(file?.modifiedAt || exif?.modifiedAt)?.toISO();
      }
      return null;
    } catch (_error) {
      return null;
    }
  }

  // INFO: All these different handlings of properties is because of the different sources of data we can get from the platforms.
  async getFileMetadata(file?: any, exif?: any) {
    try {
      const date = new Date();
      const metadata: any = {
        date: file?.lastModifiedDate?.toISOString() || date.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      exif = file?.exif || exif;
      if (exif) {
        const latitude = this.getLatitude(exif);
        const longitude = this.getLongitude(exif);
        const exifDate = this.getDate(exif, file);
        metadata.latitude = latitude;
        metadata.longitude = longitude;
        metadata.date = exifDate;

        if (latitude !== null || longitude !== null || date !== null) {
          return metadata;
        }
      }

      if (metadata.date === null) {
        metadata.date = file?.lastModifiedDate?.toISOString() || date.toISOString();
      }

      if (Capacitor.isNativePlatform()) {
        await Geolocation.requestPermissions();
      }

      await Geolocation.checkPermissions();
      try {
        const deviceLocation = await this.locationProvider.get();
        return {
          ...metadata,
          latitude: deviceLocation?.latitude?.toString() || null,
          longitude: deviceLocation?.longitude?.toString() || null,
        };
      } catch (_error) {
        return metadata;
      }
    } catch (_error) {
      return null;
    }
  }

  private filterMetadata(metadata: any): any {
    if (!metadata) {return null;}

    const filteredMetadata = Object.fromEntries(
      Object.entries(metadata).filter(([_, value]) => value !== null && value !== '' && value !== undefined)
    );

    return Object.keys(filteredMetadata).length > 0 ? filteredMetadata : null;
  }

  async uploadFileToS3(fileData, key, contentType, file?: any, exif?): Promise<any> {
    // Convert data to Uint8Array for AWS SDK v3 compatibility
    let body: Uint8Array | string;

    if (typeof fileData === 'string' && fileData.includes('base64,')) {
      // Handle base64 data URI
      try {
        const base64Data = fileData.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        body = bytes;
      } catch (err) {
        console.error('Error converting base64 data:', err);
        body = fileData;
      }
    } else if (fileData instanceof Blob) {
      // Handle File/Blob (e.g. video uploads) — convert to Uint8Array for AWS SDK v3 compatibility
      const arrayBuffer = await fileData.arrayBuffer();
      body = new Uint8Array(arrayBuffer);
    } else {
      body = fileData;
    }

    const metadata = await this.getFileMetadata(file, exif);

    // Split bucket name and folder prefix for AWS SDK v3 compatibility
    const bucketConfig = environment.aws_s3_bucket.split('/');
    const bucketName = bucketConfig[0]; // e.g., "samantha-temp-file-uploads"
    const folderPrefix = bucketConfig[1]; // e.g., "sandbox3", "production", etc.

    // Prepend folder prefix to key if it exists
    const finalKey = folderPrefix ? `${folderPrefix}/${key}` : key;

    const uploadParams: any = {
      Bucket: bucketName,
      Key: finalKey,
      Body: body,
      ContentType: contentType,
    };

    const filteredMetadata = this.filterMetadata(metadata);
    if (filteredMetadata) {
      uploadParams.Metadata = filteredMetadata;
    }

    try {
      const { PutObjectCommand } = await import('@aws-sdk/client-s3');
      const command = new PutObjectCommand(uploadParams);
      const client = await this.getS3Client();
      await client.send(command);

      return {
        Bucket: bucketName,
        Key: finalKey,
        Location: `https://${bucketName}.s3.${environment.aws_s3_region_bucket}.amazonaws.com/${finalKey}`,
      };
    } catch (err) {
      console.error('Upload error:', err);
      Sentry.captureException(err);
      throw err;
    }
  }

  async getObjectMetadata(objectUrl: string): Promise<S3ObjectMetadata> {
    if (!objectUrl || objectUrl?.startsWith('data:')) {
      return {
        latitude: null,
        longitude: null,
        date: null,
        timezone: null,
        address: null,
      };
    }

    const awsObjectUrl = this.parseGumletUrl(objectUrl);
    const objectResponse = await fetch(awsObjectUrl);
    const objectHeaders = objectResponse.headers;

    return {
      latitude: objectHeaders.get('x-amz-meta-latitude'),
      longitude: objectHeaders.get('x-amz-meta-longitude'),
      date: objectHeaders.get('x-amz-meta-date'),
      timezone: objectHeaders.get('x-amz-meta-timezone'),
      address: objectHeaders.get('x-amz-meta-address'),
    }
  }

  parseGumletUrl(url: string): string {
    if(!url.includes(GUMLET_URL)) {return url;}

    return url.replace(GUMLET_URL, AWS_S3_URL);
  }

  uploadImageToS3(picture, key, file?: any, exif?): Promise<any> {
    return this.uploadFileToS3(picture, key, 'image/jpeg', file, exif);
  }

  uploadVideoToS3(picture, key, file?: any, exif?): Promise<S3UploadResponse> {
    return this.uploadFileToS3(picture, key, 'video/mp4', file, exif);
  }

  dataURItoBlob(dataURI, contentType) {
    try {
      const binary = atob(dataURI.split(',')[1]);
      const array: number[] = [];
      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
      }

      return new Blob([new Uint8Array(array)], {type: contentType});
    } catch {
      return null;
    }
  }

  private async getS3Client(): Promise<S3ClientType> {
    const { S3Client } = await import('@aws-sdk/client-s3');
    return new S3Client({
      region: environment.aws_s3_region_bucket,
      credentials: {
        accessKeyId: environment.aws_access_key_id,
        secretAccessKey: environment.aws_secret_access_key,
      },
    });
  }

}
